import { Fragment, ReactNode, useEffect, useState } from "react";
import { Task, TaskLink } from "../../../api/Tasks";
import { applyOrder } from "../../../misc/dragOrder";
import { useTaskContextMenu } from "../TaskContextMenu";
import { vibrate } from "../useCompleteTask";
import { PanelAdd, PanelSection } from "./PanelSection";
import { TaskLinkRow } from "./TaskLinkRow";

// TaskLinkSection is the frame the panel's task lists share: the tasks, and whatever
// control adds another one.
export function TaskLinkSection(props: {
    label?: string;
    value: TaskLink[];
    busy: boolean;
    onToggle: (link: TaskLink, completed: boolean) => void;
    onRemove: (link: TaskLink) => void;
    onArchive: (link: TaskLink) => void;

    // onPromote adds "Make standalone task" to each row's menu
    onPromote?: (link: TaskLink) => void;

    // removeLabel names what the close button does, since a section either takes an
    // item out of a list or ends the task's membership of one
    removeLabel?: string;

    // removeArchives marks a list where removing a task archives it, so the menu's
    // Archive item stands in for Remove
    removeArchives?: boolean;

    // onOpen puts the task behind its title, so clicking the title opens it. A list
    // that renames in place goes without.
    onOpen?: (link: TaskLink) => void;

    // onRename turns each title into a field that is edited in place
    onRename?: (link: TaskLink, title: string) => void;

    // onEnter runs when a row's title field is saved with the Enter key
    onEnter?: (link: TaskLink) => void;

    // insert is a row shown after the task with the id "after", such as a draft
    insert?: { after: number; element: ReactNode };

    // onSchedule puts a calendar button on each row, which is where the date is set. It
    // takes the whole date, "" for none.
    onSchedule?: (link: TaskLink, date: { dueDate: string; dueTime: string }) => void;

    // onReorder turns on drag-sorting and takes the ids in their new order. Without
    // it the rows are fixed in the order they were given.
    onReorder?: (ids: number[]) => void;

    // compact draws each row tighter (see TaskLinkRow)
    compact?: boolean;

    // completedDates shows the day each task was completed in place of its due date
    completedDates?: boolean;

    // expectedDate is passed to each row (see TaskLinkRow)
    expectedDate?: string | null;

    // children is the control that adds an item, shown under the list
    children: ReactNode;
}) {
    const menu = useTaskContextMenu<TaskLink>({
        onArchive: props.onArchive,
        onToggle: props.onToggle,
        onPromote: props.onPromote,

        onRemove: props.removeArchives ? undefined : props.onRemove,
        removeLabel: props.removeLabel,
    });

    const [dragId, setDragId] = useState<number | null>(null);

    // the preview stands in for the server order until the list itself changes
    const [order, setOrder] = useState<number[] | null>(null);
    const ids = props.value.map(link => link.id).join(",");
    useEffect(() => setOrder(null), [ids]);

    const value = props.onReorder ? applyOrder(props.value, order) : props.value;

    const dragOver = (overId: number) => {
        if (dragId === null || dragId === overId) return;

        const next = value.map(link => link.id);
        const from = next.indexOf(dragId);
        const to = next.indexOf(overId);
        if (from < 0 || to < 0 || from === to) return;

        next.splice(from, 1);
        next.splice(to, 0, dragId);
        setOrder(next);
    };

    return (
        <PanelSection label={props.label}>
            {value.flatMap(link => [
                <TaskLinkRow
                    key={link.id}
                    value={link}
                    busy={props.busy}
                    removeLabel={props.removeLabel}
                    compact={props.compact}
                    completedDate={props.completedDates}
                    expectedDate={props.expectedDate}
                    // a task on a team the user isn't on can't be opened
                    onOpen={props.onOpen && !link.noAccess ? () => props.onOpen && props.onOpen(link) : undefined}
                    onToggle={completed => props.onToggle(link, completed)}
                    onRemove={() => props.onRemove(link)}
                    onContextMenu={e => menu.open(link, e)}
                    onMenu={point => menu.openAt(link, point)}
                    onHold={point => {
                        vibrate();
                        menu.openAt(link, point);
                    }}
                    onRename={props.onRename
                        ? title => props.onRename && props.onRename(link, title)
                        : undefined}
                    onEnter={props.onEnter ? () => props.onEnter && props.onEnter(link) : undefined}
                    onSchedule={props.onSchedule
                        ? date => props.onSchedule && props.onSchedule(link, date)
                        : undefined}
                    draggable={!!props.onReorder}
                    onDragStart={() => setDragId(link.id)}
                    onDragEnd={() => setDragId(null)}
                    onDragOver={e => {
                        if (dragId === null) return;

                        e.preventDefault();
                        dragOver(link.id);
                    }}
                    onDrop={e => {
                        if (dragId === null) return;

                        e.preventDefault();
                        if (order && props.onReorder) props.onReorder(order);
                        setDragId(null);
                    }}
                />,

                // one flat list keyed by id, so the insert keeps its place (and its
                // focus) while rows are added in front of it
                props.insert && props.insert.after === link.id
                    ? <Fragment key="insert">{props.insert.element}</Fragment>
                    : null,
            ])}

            <PanelAdd>{props.children}</PanelAdd>
            {menu.element}
        </PanelSection>
    );
}

export function toLink(task: Task): TaskLink {
    return {
        id: task.id,
        title: task.title,
        dueDate: task.dueDate,
        dueTime: task.dueTime,
        completedAt: task.completedAt,
    };
}
